import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Printer, FileText, ArrowRight, RefreshCw } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { clsx } from 'clsx';
import { opdApi } from '@/lib/api';
import { useBranding } from '@/hooks/useBranding';
import { useAuthStore } from '@/store/auth.store';
import ManualPrescriptionPrint, { ManualRxData } from '@/shared/components/prescription/ManualPrescriptionPrint';
import DigitalPrescriptionPrint, { DigitalRxData } from '@/shared/components/prescription/DigitalPrescriptionPrint';

// ── Local helpers ──────────────────────────────────────────────────

function fmtDate(d?: string | null) {
  if (!d) return '—';
  try { return format(parseISO(d), 'dd MMM yyyy'); } catch { return d; }
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    waiting:     'bg-yellow-100 text-yellow-700',
    in_progress: 'bg-blue-100 text-blue-700',
    completed:   'bg-green-100 text-green-700',
    cancelled:   'bg-gray-100 text-gray-500',
    no_show:     'bg-red-100 text-red-600',
  };
  return map[status] ?? 'bg-gray-100 text-gray-500';
}

// ── Props ──────────────────────────────────────────────────────────

interface Props {
  encounter: any | null;
  patient:   any;
  onClose:   () => void;
}

// ── Component ──────────────────────────────────────────────────────

export default function OpdQuickViewDrawer({ encounter, patient, onClose }: Props) {
  const navigate = useNavigate();
  const user     = useAuthStore(s => s.user);
  const { data: branding } = useBranding();

  const [blankPrintData,   setBlankPrintData]   = useState<ManualRxData | null>(null);
  const [digitalPrintData, setDigitalPrintData] = useState<DigitalRxData | null>(null);
  const [printLoading,     setPrintLoading]     = useState<'blank' | 'digital' | null>(null);
  const [printError,       setPrintError]       = useState('');

  const isOpen = encounter !== null;

  function buildHospital() {
    return {
      name:      user?.hospitalName ?? 'Hospital',
      address:   branding?.hospital_address    ?? '',
      phone:     branding?.hospital_phone      ?? '',
      licenseNo: branding?.branding_license_no ?? undefined,
      tagline:   branding?.branding_tagline    ?? undefined,
      logoUrl:   branding?.branding_logo       ?? undefined,
      settings:  branding,
    };
  }

  async function handleBlankPrint() {
    if (!encounter) return;
    setPrintLoading('blank');
    setPrintError('');
    try {
      const { data: enc } = await opdApi.getEncounter(encounter.id);
      const manualData: ManualRxData = {
        hospital: buildHospital(),
        doctor: {
          name:           enc.doctor?.name           ?? encounter.doctor_name ?? 'Doctor',
          qualifications: enc.doctor?.reg_number     ? `Reg. ${enc.doctor.reg_number}` : '',
          specialization: enc.doctor?.specialization ?? undefined,
          regNo:          enc.doctor?.reg_number     ?? undefined,
        },
        patient: {
          name:   patient?.name        ?? '—',
          uid:    patient?.patient_uid ?? '—',
          age:    patient?.age_years   ?? null,
          gender: patient?.gender      ?? 'unknown',
        },
        encounterId: enc.id,
        date:        enc.encounter_date ?? undefined,
        vitals: enc.vitals ? {
          bp_systolic:  enc.vitals.bp_systolic  ?? null,
          bp_diastolic: enc.vitals.bp_diastolic ?? null,
          pulse:        enc.vitals.pulse        ?? null,
          temp_c:       enc.vitals.temp_c       ?? null,
          spo2:         enc.vitals.spo2         ?? null,
          weight_kg:    enc.vitals.weight_kg    ?? null,
          height_cm:    enc.vitals.height_cm    ?? null,
          rr:           enc.vitals.rr           ?? null,
        } : undefined,
      };
      setBlankPrintData(manualData);
    } catch (err: any) {
      console.error('[DRAWER] Blank print fetch failed:', err);
      setPrintError(err?.response?.data?.error ?? 'Failed to load encounter data. Please try again.');
    } finally {
      setPrintLoading(null);
    }
  }

  async function handleDigitalPrint() {
    if (!encounter) return;
    setPrintLoading('digital');
    setPrintError('');
    try {
      const { data: enc } = await opdApi.getEncounter(encounter.id);
      const digitalData: DigitalRxData = {
        hospital: buildHospital(),
        doctor: {
          name:           enc.doctor?.name           ?? encounter.doctor_name ?? 'Doctor',
          qualifications: enc.doctor?.reg_number     ? `Reg. ${enc.doctor.reg_number}` : '',
          specialization: enc.doctor?.specialization ?? undefined,
          regNo:          enc.doctor?.reg_number     ?? undefined,
        },
        patient: {
          name:     patient?.name        ?? '—',
          uid:      patient?.patient_uid ?? '—',
          age:      patient?.age_years   ?? null,
          gender:   patient?.gender      ?? 'unknown',
          allergies: Array.isArray(patient?.known_allergies)
            ? patient.known_allergies
            : [],
        },
        encounter: {
          id:                 enc.id,
          encounter_no:       enc.encounter_no       ?? undefined,
          encounter_date:     enc.encounter_date     ?? undefined,
          chief_complaint:    enc.chief_complaint    ?? undefined,
          soap_assessment:    enc.soap_assessment    ?? undefined,
          soap_plan:          enc.soap_plan          ?? undefined,
          primary_icd10_code: enc.primary_icd10_code ?? undefined,
          primary_icd10_desc: enc.primary_icd10_desc ?? undefined,
          follow_up_date:     enc.follow_up_date     ?? undefined,
          referral_to:        enc.referral_to        ?? undefined,
        },
        vitals: enc.vitals ? {
          bp_systolic:  enc.vitals.bp_systolic  ?? null,
          bp_diastolic: enc.vitals.bp_diastolic ?? null,
          pulse:        enc.vitals.pulse        ?? null,
          temp_c:       enc.vitals.temp_c       ?? null,
          spo2:         enc.vitals.spo2         ?? null,
          weight_kg:    enc.vitals.weight_kg    ?? null,
          height_cm:    enc.vitals.height_cm    ?? null,
          rr:           enc.vitals.rr           ?? null,
        } : undefined,
        prescription: enc.prescription ?? null,
      };
      setDigitalPrintData(digitalData);
    } catch (err: any) {
      console.error('[DRAWER] Digital print fetch failed:', err);
      setPrintError(err?.response?.data?.error ?? 'Failed to load prescription data. Please try again.');
    } finally {
      setPrintLoading(null);
    }
  }

  const isCompleted = encounter?.status === 'completed';

  return (
    <>
      {/* ── Backdrop ────────────────────────────────────────────── */}
      <div
        aria-hidden="true"
        className={clsx(
          'fixed inset-0 bg-black/40 z-40 transition-opacity duration-300',
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
        onClick={onClose}
      />

      {/* ── Slide-out drawer ────────────────────────────────────── */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="OPD Encounter Quick View"
        className={clsx(
          'fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl z-50',
          'flex flex-col transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100 flex-shrink-0">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <code className="font-mono text-sm font-bold text-navy bg-navy/5 px-2 py-0.5 rounded tracking-wide">
                {encounter?.encounter_no ?? '—'}
              </code>
              {encounter?.appointment_no && (
                <code className="font-mono text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                  Ref: {encounter.appointment_no}
                </code>
              )}
            </div>
            <div className="text-sm font-semibold text-gray-800 truncate">
              {patient?.name ?? '—'}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {fmtDate(encounter?.event_date ?? encounter?.encounter_date)}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close drawer"
            className="ml-3 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition flex-shrink-0"
          >
            <X size={16}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <p className="text-2xs text-gray-400 uppercase tracking-widest font-semibold mb-4">
            Quick Actions
          </p>

          <div className="space-y-3">

            {/* Action 1 — Print Blank / Manual */}
            <button
              type="button"
              onClick={handleBlankPrint}
              disabled={printLoading !== null}
              className={clsx(
                'w-full flex items-center gap-4 px-4 py-4',
                'border border-gray-200 rounded-xl text-left',
                'hover:bg-gray-50 hover:border-gray-300 transition',
                'disabled:opacity-40 disabled:cursor-not-allowed',
              )}
            >
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                {printLoading === 'blank'
                  ? <RefreshCw size={18} className="animate-spin text-gray-500"/>
                  : <Printer size={18} className="text-gray-600"/>
                }
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-800">
                  Print Blank / Manual Sheet
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  A4 layout with vitals grid — write Rx by hand
                </div>
              </div>
            </button>

            {/* Action 2 — Print Digital Prescription */}
            {isCompleted ? (
              <button
                type="button"
                onClick={handleDigitalPrint}
                disabled={printLoading !== null}
                className={clsx(
                  'w-full flex items-center gap-4 px-4 py-4',
                  'bg-navy text-white rounded-xl text-left',
                  'hover:bg-navy/90 transition',
                  'disabled:opacity-40 disabled:cursor-not-allowed',
                )}
              >
                <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  {printLoading === 'digital'
                    ? <RefreshCw size={18} className="animate-spin"/>
                    : <FileText size={18}/>
                  }
                </div>
                <div>
                  <div className="text-sm font-semibold">
                    Print Digital Prescription
                  </div>
                  <div className="text-xs text-white/60 mt-0.5">
                    Full Rx with diagnosis, medications, and advice
                  </div>
                </div>
              </button>
            ) : (
              <div
                className={clsx(
                  'w-full flex items-center gap-4 px-4 py-4',
                  'bg-gray-50 border border-dashed border-gray-200 rounded-xl',
                  'opacity-50 cursor-not-allowed select-none',
                )}
              >
                <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <FileText size={18} className="text-gray-300"/>
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-400">
                    Print Digital Prescription
                  </div>
                  <div className="text-xs text-gray-300 mt-0.5">
                    Available only for completed consultations
                  </div>
                </div>
              </div>
            )}

            {/* Action 3 — Go to OPD Queue */}
            <button
              type="button"
              onClick={() => { onClose(); navigate('/opd'); }}
              className={clsx(
                'w-full flex items-center justify-between gap-4 px-4 py-3.5',
                'rounded-xl hover:bg-gray-50 transition text-left group',
              )}
            >
              <div>
                <div className="text-sm font-medium text-navy">
                  Go to OPD Consultation Page
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  Open OPD Queue for detailed editing and clinical actions
                </div>
              </div>
              <ArrowRight
                size={15}
                className="text-gray-300 group-hover:text-navy transition flex-shrink-0"
              />
            </button>

          </div>

          {/* Error banner */}
          {printError && (
            <div className="mt-4 flex items-start gap-2 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-xs text-red-600">
              <span className="flex-shrink-0 font-bold">!</span>
              <span>{printError}</span>
            </div>
          )}
        </div>

        {/* Footer — encounter status strip */}
        {encounter && (
          <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50">
            <div className="flex items-center gap-2.5 text-xs text-gray-500 flex-wrap">
              <span className={clsx(
                'inline-flex items-center px-2 py-0.5 rounded-full font-medium capitalize text-2xs',
                statusBadge(encounter.status),
              )}>
                {encounter.status?.replace(/_/g, ' ')}
              </span>
              <span className="text-gray-300">·</span>
              <span>{encounter.doctor_name ?? '—'}</span>
              {encounter.doctor_specialization && (
                <>
                  <span className="text-gray-300">·</span>
                  <span>{encounter.doctor_specialization}</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Print portals ──────────────────────────────────────── */}
      {blankPrintData && (
        <ManualPrescriptionPrint
          data={blankPrintData}
          onDone={() => setBlankPrintData(null)}
        />
      )}
      {digitalPrintData && (
        <DigitalPrescriptionPrint
          data={digitalPrintData}
          onDone={() => setDigitalPrintData(null)}
        />
      )}
    </>
  );
}
